import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, Grid, Link, Stack, Typography } from "@mui/material";
import CopyableTypography from "components/Typography/CopyableTypography";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ReviewerStatus } from "generated-graphql/graphql";
import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { formatDateLikeForDisplay } from "util/dates";

import { getStaffAssigneeName, StaffAssignee } from "./helpers/displayHelpers";
import { visibleReviewers } from "./helpers/reviewerHelpers";
import { ReviewerSignerDisplay, ReviewerSignerPerson } from "./ReviewerSignerDisplay";

export interface SubmissionFacilityDisplayInfo {
  id?: string;
  display: string;
  epaId: string | null;
  href: string | null;
}

type ReviewParticipant = ReviewerSignerPerson & {
  id: string;
  isStaff?: boolean | null;
  reviewerStatus?: ReviewerStatus | null;
};

function EpaIdValue({ epaId }: { epaId?: string | null }) {
  if (!epaId) return <Typography variant="body1">—</Typography>;
  return <CopyableTypography text={epaId} variant="body1" />;
}

export function SubmissionReadOnlySummary({
  tenantId,
  isStaff,
  facilities,
  reviewers,
  signer,
  title,
  dueDate,
  recipient,
  staffAssignee,
  manifest,
  notes,
  showTitle = true,
  showDueDate = true,
  showStaffAssignee = true,
  showNotes = true,
  showRecipient = true,
  signerExtra,
}: {
  tenantId: string;
  isStaff: boolean;
  facilities: SubmissionFacilityDisplayInfo[];
  reviewers: ReviewParticipant[];
  signer?: ReviewParticipant;
  title?: string | null;
  dueDate?: string | null;
  recipient?: string | null;
  staffAssignee?: StaffAssignee | null;
  manifest?: { id: string; manifestTrackingNumber: string } | null;
  notes?: string | null;
  showTitle?: boolean;
  showDueDate?: boolean;
  showStaffAssignee?: boolean;
  showNotes?: boolean;
  showRecipient?: boolean;
  signerExtra?: ReactNode;
}) {
  const shownReviewers = visibleReviewers(reviewers, isStaff);

  return (
    <Stack spacing={3}>
      {showTitle && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Title
          </Typography>
          <Typography variant="body1">{title}</Typography>
        </Box>
      )}

      {facilities.length > 0 && (
        <Box>
          {/* One header over the list; every site is a row beneath it. */}
          <Grid container spacing={3} sx={{ mb: 0.5 }}>
            <Grid size={6}>
              <Typography variant="body2" color="text.secondary">
                {facilities.length === 1 ? "Facility" : "Facilities"}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="body2" color="text.secondary">
                EPA ID
              </Typography>
            </Grid>
          </Grid>
          <Stack spacing={1}>
            {facilities.map((facility, index) => (
              <Grid container spacing={3} key={facility.id ?? index}>
                <Grid size={6}>
                  {facility.href ? (
                    <Link
                      href={facility.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body1"
                      underline="hover"
                      sx={{
                        wordBreak: "break-word",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {facility.display}
                      <OpenInNew sx={{ fontSize: 16 }} />
                    </Link>
                  ) : (
                    <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                      {facility.display}
                    </Typography>
                  )}
                </Grid>
                <Grid size={6}>
                  <EpaIdValue epaId={facility.epaId} />
                </Grid>
              </Grid>
            ))}
          </Stack>
        </Box>
      )}

      {manifest && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Manifest
          </Typography>
          <Link
            component={RouterLink}
            to={`/o/${tenantId}/waste/manifests/${manifest.id}`}
            variant="body1"
            underline="hover"
          >
            {manifest.manifestTrackingNumber}
          </Link>
        </Box>
      )}

      {(showDueDate || showRecipient) && (
        <Grid container spacing={3}>
          {showDueDate && (
            <Grid size={6}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Due Date
                </Typography>
                <Typography variant="body1">
                  {dueDate ? formatDateLikeForDisplay(dueDate) : ""}
                </Typography>
              </Box>
            </Grid>
          )}
          {showRecipient && (
            <Grid size={6}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Recipient
                </Typography>
                <Typography variant="body1">
                  {recipient ? prettyPrintEnumValue(recipient) : "—"}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {isStaff && showStaffAssignee && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Encamp Assignee
          </Typography>
          <Typography variant="body1" color={staffAssignee ? "text.primary" : "text.secondary"}>
            {getStaffAssigneeName(staffAssignee)}
          </Typography>
        </Box>
      )}

      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Reviewers
        </Typography>
        {shownReviewers.length > 0 ? (
          <Stack spacing={0.5}>
            {shownReviewers.map((reviewer) => (
              <ReviewerSignerDisplay
                key={reviewer.id}
                person={reviewer}
                isCompleted={reviewer.reviewerStatus != null}
                approved={reviewer.reviewerStatus === ReviewerStatus.Approved}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body1" color="text.secondary">
            No reviewers assigned
          </Typography>
        )}
      </Box>

      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Signer
        </Typography>
        {signer ? (
          <Stack spacing={1}>
            <ReviewerSignerDisplay
              person={signer}
              isCompleted={signer.reviewerStatus != null}
              approved={signer.reviewerStatus === ReviewerStatus.Approved}
            />
            {signerExtra}
          </Stack>
        ) : (
          <Typography variant="body1" color="text.secondary">
            No signer assigned
          </Typography>
        )}
      </Box>

      {showNotes && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Notes
          </Typography>
          {notes ? (
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {notes}
            </Typography>
          ) : (
            <Typography variant="body1" color="text.secondary">
              No notes
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
}
